import { Fragment, useEffect, useState } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download } from "lucide-react";
import { downloadExport } from "../operations/shared/downloadExport";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function yearOptions() {
  const y = new Date().getFullYear();
  return [y - 1, y, y + 1];
}

const VERIFICATION_STYLES = {
  ai_verified: "bg-emerald-600",
  verified_manual: "bg-emerald-600",
  flagged: "bg-red-600",
  pending_review: "bg-amber-500",
  ai_check_pending: "bg-slate-400",
  not_applicable: "bg-slate-300 text-slate-700",
};

function ShiftCells({ cell, testId }) {
  if (!cell.has_entry) {
    return (
      <>
        <TableCell className="text-slate-400" data-testid={`${testId}-operator`}>—</TableCell>
        <TableCell className="text-slate-400">—</TableCell>
        <TableCell className="text-slate-400">—</TableCell>
        <TableCell className="text-slate-400">—</TableCell>
        <TableCell />
        <TableCell className="text-slate-400">—</TableCell>
      </>
    );
  }
  const reading = cell.reading_status === "verified" ? cell.reading : "Pending Verification";
  const closing =
    cell.hours_status === "computed" ? cell.closing_reading : cell.hours_status === "no_entry" ? "—" : "Pending Verification";
  const hours =
    cell.hours_status === "computed" ? `${cell.hours} hrs` : cell.hours_status === "no_entry" ? "—" : "Pending Verification";
  return (
    <>
      <TableCell data-testid={`${testId}-operator`}>{cell.operator || "—"}</TableCell>
      <TableCell className="font-mono">{reading}</TableCell>
      <TableCell className="font-mono">{closing}</TableCell>
      <TableCell className="font-mono">{hours}</TableCell>
      <TableCell>
        {cell.verification && (
          <Badge className={`${VERIFICATION_STYLES[cell.verification.state]} text-white`} data-testid={`${testId}-verification`}>
            {cell.verification.label}
            {cell.verification.reviewed_by_name ? ` (${cell.verification.reviewed_by_name})` : ""}
          </Badge>
        )}
      </TableCell>
      <TableCell className="text-xs text-slate-500 max-w-[180px] truncate">{cell.remarks || "—"}</TableCell>
    </>
  );
}

export default function DprReportTab() {
  const [sites, setSites] = useState([]);
  const [siteFilter, setSiteFilter] = useState("");
  const [assets, setAssets] = useState([]);
  const [assetId, setAssetId] = useState("");
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    client.get("/site-master/sites", { params: { all: true } }).then((res) => setSites(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    client
      .get("/dataflow/dpr/assets", { params: siteFilter ? { site_id: siteFilter } : {} })
      .then((res) => {
        setAssets(res.data);
        setAssetId((prev) => (res.data.find((a) => a.asset_id === prev) ? prev : ""));
      })
      .catch(() => {});
  }, [siteFilter]);

  const load = () => {
    if (!assetId) return toast.error("Select a machine first");
    setLoading(true);
    setReport(null);
    client
      .get("/dataflow/dpr/report", { params: { asset_id: assetId, month, year } })
      .then((res) => setReport(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  const exportXlsx = async () => {
    if (!assetId) return toast.error("Select a machine first");
    try {
      await downloadExport("/dataflow/dpr/report/export", { asset_id: assetId, month, year });
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="dpr-report-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">Site (optional)</p>
          <SearchableSelect testId="dpr-site-filter" className="w-56" value={siteFilter || "all"} onValueChange={(v) => setSiteFilter(v === "all" ? "" : v)} placeholder="All sites"
  options={[{ value: "all", label: "All sites", pinned: true, testId: "dpr-site-filter-option-all" }, ...sites.map((s) => ({ value: s.site_id, label: s.display_label, testId: `dpr-site-filter-option-${s.site_id}` }))]} />
        </div>
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">Machine *</p>
          <SearchableSelect testId="dpr-asset-select" className="w-64" value={assetId} onValueChange={setAssetId} placeholder="Select machine"
  options={assets.map((a) => ({ value: a.asset_id, label: `${a.label} — ${a.site_label}`, testId: `dpr-asset-option-${a.asset_id}` }))} />
        </div>
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">Month</p>
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger data-testid="dpr-month-select" className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map((m, idx) => (
                <SelectItem key={m} value={String(idx + 1)}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">Year</p>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger data-testid="dpr-year-select" className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions().map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={load} disabled={loading || !assetId} data-testid="dpr-load-button" className="bg-amber-600 hover:bg-amber-700">
          {loading ? "Loading..." : "Load Report"}
        </Button>
        <Button variant="outline" onClick={exportXlsx} disabled={!assetId} data-testid="dpr-export-button">
          <Download className="h-4 w-4 mr-1.5" /> Export Excel
        </Button>
      </div>

      {report && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="dpr-summary-machine">
              <p className="text-xs text-slate-500">Machine</p>
              <p className="text-sm font-semibold">{report.asset.machine_name}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="dpr-summary-site">
              <p className="text-xs text-slate-500">Site</p>
              <p className="text-sm font-semibold">{report.asset.site_label || "—"}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="dpr-summary-total-hours">
              <p className="text-xs text-slate-500">Total Hours ({MONTHS[report.month - 1]} {report.year})</p>
              <p className="text-sm font-semibold">{report.total_hours} hrs</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="dpr-summary-outstanding">
              <p className="text-xs text-slate-500">Outstanding Reviews</p>
              <div className="text-sm font-semibold">
                {report.outstanding_reviews > 0 ? <Badge className="bg-red-600">{report.outstanding_reviews}</Badge> : "0"}
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500" data-testid="dpr-assigned-operators">
            Assigned Operators:{" "}
            {report.assigned_operators.length ? report.assigned_operators.map((o) => o.name).join(", ") : "None assigned"}
          </p>

          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <Table data-testid="dpr-report-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Shift</TableHead>
                  <TableHead>Operator</TableHead>
                  <TableHead>Starting</TableHead>
                  <TableHead>Closing</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Verification</TableHead>
                  <TableHead>Remarks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.days.map((d) => (
                  <Fragment key={d.date}>
                    <TableRow data-testid={`dpr-row-${d.date}-day`}>
                      <TableCell rowSpan={2} className="font-medium align-top">
                        {d.day}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">Day</TableCell>
                      <ShiftCells cell={d.day_shift} testId={`dpr-row-${d.date}-day`} />
                    </TableRow>
                    <TableRow data-testid={`dpr-row-${d.date}-night`}>
                      <TableCell className="text-xs text-slate-500">Night</TableCell>
                      <ShiftCells cell={d.night_shift} testId={`dpr-row-${d.date}-night`} />
                    </TableRow>
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
